import {store} from '../redux/store';
import {
  defaultProjectTitle,
  changeName,
  changePath,
  currentConfigVersion,
  minimumConfigVersion
} from '../redux/project-slice';
import {
  dialog,
  fs,
  app,
  ipcRenderer,
  isStaticSite
} from '../electron-modules';
import slash from 'slash';
import path from 'path';
import {isEqual} from 'lodash';
import { upgradeConfig } from '../redux/config-upgrade';
import { configFilter } from '../file-extensions';
import semver from "semver";
import { t } from '../localization';

export const saveProjectAs = () => {
  const projectName = store.getState().project.name;
  const localizedProjectName = t(projectName);
  const defaultPath = `${localizedProjectName}.json`;

  return dialog.showSaveDialog({
    title: t("dialog.save-project"),
    defaultPath,
    filters: configFilter,
  }).then((result) => {
    if (result.canceled) {
      return false;
    }

    const savePath = slash(result.filePath);
    store.dispatch(changePath(savePath));

    // Update the title of the project to be the filename
    if (projectName === defaultProjectTitle) {
      const fileName = path.basename(savePath);
      const extension = path.extname(savePath);
      const nameWithoutExt = path.basename(fileName, extension);
      store.dispatch(changeName(nameWithoutExt));
    }

    const state = store.getState();
    writeProject(state);
    return true;
  }).catch(() => {
    dialog.showErrorBox(t("dialog.project-save-title"),
      t("dialog.project-save-message"));
    return false;
  });
};

export const saveProject = (allowSaveAs = true) => {
  return new Promise(resolve => {
    const state = store.getState();

    if (state.project.path === '') {
      if (allowSaveAs) {
        saveProjectAs().then(result => {
          resolve(result);
        });
      }
      return;
    } else {
      writeProject(state);
      resolve(true);
    }
  });
};

export const loadProjectJSON = (data, staticURL = null) => {
  const projectJSON = JSON.parse(data);

  store.dispatch({
    type: 'LOAD',
    payload: {projectJSON, staticURL}
  });
};

export const getConfigVersion = (config) => {
  let version = config.project.configVersion;
  if (typeof version === "number") {
    version = `${String(version)}.0`;
  }

  return version;
};

export const setWindowHash = (hash) => {
  window.history.pushState({}, '', "#" + hash);
};

export const clearWindowHash = () => {
  window.location.hash = "";
};

export const updateFromJSON = (state) => {
  state.assets = state.assets.map(asset => {
    return {
      ...asset,
      fromJSON: true
    };
  });

  return state;
};

export const loadProjectFile = (filePath) => {
  try {
    const data = fs.readFileSync(filePath, 'utf8');

    // Update json path based on where the json was loaded from
    let projectJSON = JSON.parse(data);
    projectJSON.project.path = filePath;

    let configVersion = getConfigVersion(projectJSON);

    // Update the fromJSON state
    projectJSON = updateFromJSON(projectJSON);

    // Very old versions can't be upgraded
    if (semver.lt(configVersion, minimumConfigVersion)) {
      dialog.showErrorBox(t("dialog.general-error-title"),
        t("dialog.legacy-version-error", {version: configVersion}));
      return false;
    }

    // Upgrade project configuration to the most recent version
    if (semver.lt(configVersion, currentConfigVersion)) {
      projectJSON = upgradeConfig(projectJSON);
    }

    clearWindowHash();

    const projectString = JSON.stringify(projectJSON);
    loadProjectJSON(projectString);
    return true;
  } catch (err) {
    dialog.showErrorBox(t("dialog.general-error-title"),
      t("dialog.project-load-message"));
    return false;
  }
};

export const loadProject = () => {
  dialog.showOpenDialog({
    properties: ['openFile'],
    filters: configFilter
  }).then(result => {
    if (result.canceled) {
      return;
    }

    const filePath = slash(result.filePaths[0]);
    loadProjectFile(filePath);
  }).catch(error => {
    console.log(error);
  });
};

export const newProject = () => {
  dialog.showMessageBox({
    type: 'question',
    title: t("dialog.new-project-title"),
    message: t("dialog.new-project-unsaved"),
    buttons: [
      t("buttons.cancel"),
      t("buttons.new-project")
    ],
    noLink: true
  })
    .then(result => {
      if (result.response === 0) return;
      store.dispatch({type: 'NEW'});
    });
};

const writeProject = (state) => {
  const name = state.project.path;
  const string = JSON.stringify(state, null, 2);

  fs.writeFile(name, string, (err) => {
    if (err) {
      throw Error('an error occurred while saving the project');
    }
  });
};

const saveAndExit = () => {
  const state = store.getState();

  if (state.project.path !== "") {
    const data = fs.readFileSync(state.project.path, "utf8");
    let projectJSON = JSON.parse(data);
    projectJSON.project.modified = state.project.modified;
  }

  saveProject().then((saved) => {

    if (saved) {
      app.exit();
    }
  });
};

if (!isStaticSite) {
  ipcRenderer.on("exit-with-changes", () => {
    const state = store.getState();
    let hasChanged;

    if (state.project.path !== "") {
      // Compare current state to saved json state
      const data = fs.readFileSync(state.project.path, "utf8");
      let projectJSON = JSON.parse(data);
      projectJSON.project.modified = state.project.modified;
      hasChanged = !isEqual(state, projectJSON);
    } else {
      // Dont prompt user unless assets are loaded
      hasChanged = state.assets.length !== 0;
    }

    if (!hasChanged) {
      app.exit();
      return;
    }

    dialog.showMessageBox({
      type: "question",
      message: t("dialog.exit-save-changes"),
      buttons: [
        t("buttons.save"),
        t("buttons.dont-save")
      ],
      defaultId: 2,
      noLink: true
    })
      .then((result) => {
        if (result.response === 1) {
          app.exit();
        } else if (result.response === 0) {
          saveAndExit();
        }
      });
  });

  ipcRenderer.on("new-project", () => {
    newProject();;
  });

  ipcRenderer.on("load-project", () => {
    loadProject();
  });

  ipcRenderer.on("save-project", () => {
    saveProject();
  });

  ipcRenderer.on("save-project-as", () => {
    saveProjectAs();
  });
}